import type { StepContentProps as MuiStepContentProps } from '@mui/material/StepContent';
import {
  type ComponentType,
  type ReactElement,
  createElement,
  isValidElement,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { BaseStep } from './BaseStep';
import { BaseStepper } from './BaseStepper';

export type SlabStep<StepsType extends string> = {
  label: StepsType;
  disabled?: boolean;
  hidden?: boolean;
  error?: boolean;
  completed?: boolean;
  content: ComponentType | ReactElement;
  transitionProps?: MuiStepContentProps['TransitionProps'];
};

type SlabStepperProps<StepsType extends string> = {
  steps: SlabStep<StepsType>[];
  activeStep: StepsType;
  onChangeStep(step: StepsType): void;
};

export const SlabStepper = <StepsType extends string>({
  steps,
  onChangeStep,
  activeStep,
}: SlabStepperProps<StepsType>): ReactElement => {
  const activeStepIndex = useMemo(
    () => steps.findIndex((step) => step.label === activeStep),
    [steps, activeStep],
  );

  const enteringContentRef = useRef<HTMLElement | null>(null);

  const scrollToEnteringStep = useCallback((): void => {
    requestAnimationFrame(() => {
      enteringContentRef.current
        ?.closest('.MuiStep-root')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <BaseStepper activeStep={activeStepIndex} nonLinear orientation='vertical'>
      {steps.map(({ content, ...step }) => (
        <BaseStep
          disabled={step.disabled}
          onLabelClick={onChangeStep}
          label={step.label}
          key={step.label}
          error={step.error}
          completed={step.completed}
          transitionProps={{
            ...step.transitionProps,
            onEnter: (node: HTMLElement, isAppearing: boolean) => {
              enteringContentRef.current = node;
              step.transitionProps?.onEnter?.(node, isAppearing);
            },
            onEntered: (node: HTMLElement, isAppearing: boolean) => {
              scrollToEnteringStep();
              step.transitionProps?.onEntered?.(node, isAppearing);
            },
            onExited: (node: HTMLElement) => {
              scrollToEnteringStep();
              step.transitionProps?.onExited?.(node);
            },
          }}
        >
          {isValidElement(content) ? content : createElement(content as ComponentType)}
        </BaseStep>
      ))}
    </BaseStepper>
  );
};
